import type { api } from "@repo/convex/api";
import type { FunctionReturnType } from "convex/server";
import {
  buildDocumentStatusCellView,
  type DocumentPreReadPopover,
} from "@/features/documents/lib/document-processing-view";

type ThreadProcessingFile = NonNullable<
  FunctionReturnType<typeof api.artifactProcessing.getActiveProcessingGroupForTarget>
>["files"][number];

/** A parked file's own status, as the target's processing read hands it over. */
type ParkedAttachmentQuestion = NonNullable<ThreadProcessingFile["awaitingInput"]>;

export type AttachmentChipStatus =
  | "processing"
  | "waiting_for_you"
  | "ready"
  | "failed"
  | "unknown";

/**
 * The one place a document attachment's pipeline status becomes chip vocabulary.
 *
 * `"unknown"` is what the chip shows when nothing is known about the file — no job row has
 * been written for it yet, its placement is gone, or its run was cancelled or superseded.
 * The chip then states type and size and claims nothing. Reading that absence as `"ready"`
 * is what put "Ready to read" under a document that was still being parsed.
 *
 * `"waiting_for_you"` is its own state rather than a flavour of `"processing"`: nothing is
 * running and nothing will, until someone says how much of the file to read. A spinner over
 * that is a promise the pipeline is not keeping.
 */
export function resolveAttachmentChipStatus(
  fileStatus: ThreadProcessingFile["status"] | undefined
): AttachmentChipStatus {
  switch (fileStatus) {
    case "failed":
      return "failed";
    case "analyzed":
      return "ready";
    case "waiting":
      return "waiting_for_you";
    case "queued":
    case "analyzing":
      return "processing";
    default:
      return "unknown";
  }
}

/**
 * Whether a chat attachment's chip has a transcript to open.
 *
 * The same two facts the Documents row reads (`isTranscriptOffered`): the file is ready, and the
 * revision it reads as holds turns. `contentShape` is the server's own answer about those blocks,
 * so a recording and an uploaded transcript file are one offer and nothing here reads a file
 * extension to decide. A file still being transcribed has no words yet, and offering to open them
 * would open an empty dialog.
 */
export function resolveAttachmentOpenTranscript(
  file: { status?: ThreadProcessingFile["status"]; contentShape?: string | null } | undefined
): boolean {
  return file?.status === "analyzed" && file.contentShape === "transcript";
}

/**
 * The parked question a chat attachment's chip opens, or `null` when the file is not parked.
 *
 * The payload is built by the Documents module's own view-model
 * (`buildDocumentStatusCellView`) from the status the backend row carries, so the question a
 * chip asks is the question the Documents row asks about the same file — same wording, same
 * options, same defaults. A second builder here would let the two screens disagree about how
 * much of a file is worth reading first.
 */
export function resolveAttachmentReadScopeQuestion(
  /**
   * Structurally typed rather than `Pick<ThreadProcessingFile, …>`: the id is only ever passed
   * through to a payload that carries it as a string, and binding this to the branded row type
   * would make the mapper untestable without a real Convex id.
   */
  file:
    | { artifactId: string; name: string; awaitingInput?: ParkedAttachmentQuestion | null }
    | undefined,
  source: { fileName: string; mimeType: string; fileSizeBytes: number },
  now: number
): DocumentPreReadPopover | null {
  if (!file?.awaitingInput) return null;
  const cell = buildDocumentStatusCellView(
    {
      artifactId: file.artifactId,
      name: file.name,
      fileName: source.fileName,
      mimeType: source.mimeType,
      status: file.awaitingInput,
      fileSizeBytes: source.fileSizeBytes,
    },
    now
  );
  return cell.popover?.kind === "pre_read" ? cell.popover : null;
}
